import React from "react";
import { Download, Landmark } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DOC_LABEL } from "./KycDocRow";
import { NameMismatchWarning } from "./KycSourceBadges";

const Row = ({ k, v, testid }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 last:border-0 text-sm">
    <span className="text-slate-500">{k}</span>
    <span className="font-semibold text-[#0A2540] text-right" data-testid={testid}>{v || "—"}</span>
  </div>
);

// DigiLocker docs are issuer-signed XML — show the parsed summary instead of opening raw XML.
export function DigiLockerDocDialog({ item, onClose, onDownload, busy }) {
  const dl = item?.digilocker || {};
  const s = dl.summary || {};
  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="kyc-digilocker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A2540]">
            <Landmark size={16} className="text-[#D4AF37]" /> {DOC_LABEL[item?.doc_type] || item?.doc_type} — fetched from DigiLocker
          </DialogTitle>
          <DialogDescription>
            Issued by {dl.issuer || "the issuing authority"} · fetched {dl.fetched_at?.slice(0, 16)?.replace("T", " ")}
          </DialogDescription>
        </DialogHeader>
        {dl.name_match_status === "mismatch" && (
          <NameMismatchWarning id={item.id} documentName={dl.name} registeredName={dl.registered_name} />
        )}
        <div className="rounded-xl border border-slate-200 px-3 py-1" data-testid="kyc-digilocker-summary">
          <Row k="Name on document" v={dl.name} testid="kyc-dl-name" />
          <Row k="Registered name" v={dl.registered_name} testid="kyc-dl-registered" />
          <Row k="Name check" v={{ matched: "Matched", mismatch: "Mismatch — verify", unavailable: "Not available on document" }[dl.name_match_status] || "—"} testid="kyc-dl-match" />
          <Row k="Date of birth" v={s.dob} />
          <Row k="Gender" v={s.gender} />
          <Row k={item?.doc_type === "pan" ? "PAN (masked)" : "Aadhaar (masked)"} v={s.masked_id} />
          {s.address && <Row k="Address" v={s.address} />}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" className="rounded-lg text-xs h-8" onClick={onClose} data-testid="kyc-digilocker-dialog-close">Close</Button>
          <Button size="sm" className="rounded-lg text-xs h-8 bg-[#0A2540] text-[#D4AF37] hover:opacity-90" disabled={busy}
            onClick={() => onDownload(item)} data-testid="kyc-digilocker-download-xml">
            <Download size={12} className="mr-1" /> Signed XML
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
